import { useState, type ReactNode } from 'react';
import { ArmchairIcon, CaretUpIcon, CheckCircleIcon, HeartIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@voll-intelligence/ui-kit/ui/tabs';
import type {
  BusSeatClassGlyph,
  BusSeatFloorView,
  BusSeatMapState,
  BusSegmentPick,
} from '../models.js';
import {
  SeatDeck,
  SeatDetails,
  seatMarks,
  type BusSeatDeckCopy,
  type BusSeatTraveler,
  type SeatDeckContext,
} from './bus_seat_deck.js';
import { BusSeatLegend } from './bus_seat_glyph.js';
import { busSeatDeck, busSeatsShort, busSegmentComplete, toggleBusSeat } from './bus_seat_rules.js';
import './bus_seat_picker.css';

export type BusSeatPickerCopy = BusSeatDeckCopy & {
  /** O nome da fila de trechos ("Trechos da viagem"). */
  segmentsLabel: string;
  /** "Trecho 1" — a aba do trecho, curta para caber no celular. */
  segmentLabel: (segment: number) => string;
  /** "Trecho 1 · São Paulo → Taubaté" — o resumo embaixo da planta, quando o
   *  host conhece a parada da conexão. Ausente ⇒ `segmentLabel`. */
  segmentRouteLabel?: (segment: number) => string;
  /** "Piso 1" — só aparece no ônibus de dois andares. */
  floorLabel(floor: number): string;
  loadingLabel: string;
  /** O fornecedor falhou: "Não conseguimos carregar as poltronas deste trecho
   *  agora…". */
  errorLabel: string;
  /** Rede ou prazo estourado: "… Verifique sua conexão e tente novamente." */
  offlineLabel: string;
  /** O trecho sem marcação de assento — sem sugerir falha técnica. */
  notOfferedLabel: string;
  /** "Tentar novamente" — só na falha, quando o host sabe recarregar. */
  retryLabel: string;
  /** "Nenhuma poltrona livre neste trecho." */
  fullLabel: string;
  /** "Seguir sem assento". */
  noSeatLabel: string;
  /** O resumo de um trecho sem poltrona ("sem assento"). */
  noSeatSummary: string;
  /** "Escolha 2 poltronas" — quantas faltam no trecho em cena. */
  pendingSeatsLabel(missing: number): string;
  /** O botão que fecha a escolha ("Selecionar"). */
  confirmLabel: string;
  /** O recolher da expansão ("Fechar escolha de poltrona"). */
  collapseLabel: string;
};

/** Onde a escolha aparece: `inline` no card (a planta deitada, a frente à
 *  esquerda) ou `sheet`, a folha do celular e do tablet (a planta em pé, a
 *  frente em cima, rolando na vertical — o diálogo do pacote Flutter). */
export type BusSeatLayout = 'inline' | 'sheet';

/** O `Selecionar`: em tela cheia, o CTA grande e redondo da tela de tarifas
 *  do voo. */
const CONFIRM_LOOK = {
  inline: { size: 'sm' },
  sheet: { size: 'xl', className: 'rounded-full' },
} as const satisfies Record<BusSeatLayout, { size: string; className?: string }>;

export type BusSeatPickerProps = {
  /** O mapa de cada trecho, na ordem da viagem (1 = direto ou 1º trecho). */
  segments: readonly BusSeatMapState[];
  /** Os viajantes, na ordem da reserva — uma poltrona por viajante por trecho. */
  travelerIds: readonly string[];
  /** A resolução de cada trecho, no mesmo índice de `segments`. Controlada. */
  picks: readonly (BusSegmentPick | undefined)[];
  /** "Semi leito" — o resumo do rodapé ("14 · Semi leito"). */
  seatClassLabel: string;
  /** O preço por pessoa ("R$ 180") e a leitura dele ("só ida, por pessoa"). */
  price: { readonly label: string; readonly detail: string };
  copy: BusSeatPickerCopy;
  busy?: boolean;
  /** `inline` por padrão. Na `sheet` a folha tem o próprio voltar, então o
   *  recolher do rodapé não aparece. */
  layout?: BusSeatLayout;
  /** "Assento recomendado com base nas suas preferências" — o host passa
   *  enquanto as poltronas marcadas são as que as Preferências sugeriram. */
  suggestionLabel?: string;
  /** A forma da poltrona na planta: poltrona, ou cama no leito cama. */
  seatGlyph?: BusSeatClassGlyph;
  /** O nome e as iniciais de cada viajante (`travelerId`): as iniciais vão na
   *  poltrona escolhida, o nome no detalhe. Sem ele, a poltrona mostra o número. */
  travelers?: Readonly<Record<string, BusSeatTraveler>>;
  /** As poltronas que as Preferências sugeriram, por trecho (mesmo índice de
   *  `segments`): o coração fica nelas mesmo depois que a pessoa troca. */
  recommended?: readonly (BusSegmentPick | undefined)[];
  /** Recarrega a planta que falhou; ausente ⇒ a falha só oferece seguir sem
   *  assento. */
  onRetry?: () => void;
  onPicksChange(picks: readonly (BusSegmentPick | undefined)[]): void;
  onConfirm(): void;
  onCollapse(): void;
};

/** O que a planta mostra além das poltronas: a forma, quem viaja e a sugestão
 *  do trecho em cena. */
type SeatMapLook = {
  readonly glyph: BusSeatClassGlyph;
  readonly travelers: Readonly<Record<string, BusSeatTraveler>> | undefined;
  readonly recommended: BusSegmentPick | undefined;
};

/** O que o trecho sem planta diz: a falha (com o motivo), sem marcação no
 *  trecho, ou lotado. */
function fallbackLabel(
  state: Exclude<BusSeatMapState, { kind: 'loading' }>,
  copy: BusSeatPickerCopy,
): string {
  if (state.kind === 'error')
    return state.reason === 'offline' ? copy.offlineLabel : copy.errorLabel;
  return state.kind === 'notOffered' ? copy.notOfferedLabel : copy.fullLabel;
}

/** Falha, trecho sem marcação ou lotado: a saída é seguir sem assento — e, na
 *  falha, tentar de novo. O trecho sem marcação não é alerta: nada deu errado. */
function SegmentFallback({
  state,
  pick,
  copy,
  disabled,
  onPick,
  onRetry,
}: {
  readonly state: Exclude<BusSeatMapState, { kind: 'loading' }>;
  readonly pick: BusSegmentPick | undefined;
  readonly copy: BusSeatPickerCopy;
  readonly disabled: boolean;
  readonly onPick: (pick: BusSegmentPick | undefined) => void;
  readonly onRetry: (() => void) | undefined;
}): ReactNode {
  const failed = state.kind === 'error';
  return (
    <div className="hui-bus-seat-message" role={state.kind === 'notOffered' ? 'status' : 'alert'}>
      <span>{fallbackLabel(state, copy)}</span>
      {failed && onRetry && (
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onRetry}>
          {copy.retryLabel}
        </Button>
      )}
      <Button
        type="button"
        variant={pick?.kind === 'noSeat' ? 'default' : 'outline'}
        size="sm"
        aria-pressed={pick?.kind === 'noSeat'}
        disabled={disabled}
        onClick={() => {
          onPick({ kind: 'noSeat' });
        }}
      >
        {copy.noSeatLabel}
      </Button>
    </div>
  );
}

/** Os andares do trecho, com a legenda em cima e a linha de detalhe embaixo.
 *  A janela só entra na legenda quando algum andar tem as colunas. */
function SegmentFloors({
  floors,
  context,
  floorLabel,
}: {
  readonly floors: readonly BusSeatFloorView[];
  readonly context: Omit<SeatDeckContext, 'onInspect'>;
  readonly floorLabel: (floor: number) => string;
}): ReactNode {
  const [inspected, setInspected] = useState<string>();
  const deckContext: SeatDeckContext = { ...context, onInspect: setInspected };
  const legend = context.copy.details?.legend;
  const windows = floors.some((floor) => {
    const deck = busSeatDeck(floor);
    return !deck.estimated && deck.lanes.length > 1;
  });
  return (
    <div className="hui-bus-seat-floors">
      {legend && (
        <BusSeatLegend
          copy={legend}
          recommended={context.marks.recommended.size > 0}
          windows={windows}
        />
      )}
      {/* A chave é o rótulo do andar: os andares não têm id e nunca trocam de ordem. */}
      {floors.map((floor, index) => (
        <div key={floorLabel(index + 1)} className="grid gap-1">
          {floors.length > 1 && (
            <span className="hui-bus-seat-floor-label">{floorLabel(index + 1)}</span>
          )}
          <SeatDeck floor={floor} context={deckContext} />
        </div>
      ))}
      <SeatDetails floors={floors} code={inspected} context={deckContext} />
    </div>
  );
}

/** O mapa do trecho em cena: carregando, falha/lotado (a saída é seguir sem
 *  assento) ou os andares. */
function SegmentBody({
  state,
  pick,
  travelerIds,
  copy,
  disabled,
  layout,
  look,
  onPick,
  onRetry,
}: {
  readonly state: BusSeatMapState;
  readonly layout: BusSeatLayout;
  readonly pick: BusSegmentPick | undefined;
  readonly travelerIds: readonly string[];
  readonly copy: BusSeatPickerCopy;
  readonly disabled: boolean;
  readonly look: SeatMapLook;
  readonly onPick: (pick: BusSegmentPick | undefined) => void;
  readonly onRetry: (() => void) | undefined;
}): ReactNode {
  if (state.kind === 'loading') {
    return (
      <p role="status" className="hui-bus-seat-message">
        <Spinner aria-hidden="true" />
        {copy.loadingLabel}
      </p>
    );
  }
  if (state.kind !== 'ready' || busSeatsShort(state.floors, travelerIds.length)) {
    return (
      <SegmentFallback
        state={state}
        pick={pick}
        copy={copy}
        disabled={disabled}
        onPick={onPick}
        onRetry={onRetry}
      />
    );
  }
  return (
    <SegmentFloors
      floors={state.floors}
      floorLabel={(floor) => copy.floorLabel(floor)}
      context={{
        layout,
        glyph: look.glyph,
        marks: seatMarks(pick, look.recommended, look.travelers),
        copy,
        disabled,
        onToggle: (seat) => {
          onPick(toggleBusSeat(pick, travelerIds, seat));
        },
      }}
    />
  );
}

/** O resumo de um trecho no rodapé: "Trecho 1 / 14 · Semi leito". */
function SegmentSummary({
  index,
  pick,
  travelerIds,
  seatClassLabel,
  copy,
}: {
  readonly index: number;
  readonly pick: BusSegmentPick | undefined;
  readonly travelerIds: readonly string[];
  readonly seatClassLabel: string;
  readonly copy: BusSeatPickerCopy;
}): ReactNode {
  const seats =
    pick?.kind === 'seats' ? travelerIds.flatMap((id) => pick.seats[id] ?? []).join(', ') : '';
  const text = pick?.kind === 'noSeat' ? copy.noSeatSummary : seats || '—';
  return (
    <div className="hui-bus-seat-summary">
      <span className="hui-bus-seat-summary-label">
        {(copy.segmentRouteLabel ?? copy.segmentLabel)(index + 1)}
      </span>
      <span className="hui-bus-seat-summary-value">
        <ArmchairIcon aria-hidden="true" />
        {text} · {seatClassLabel}
      </span>
    </div>
  );
}

/** A primeira aba ainda sem resolução — é onde a escolha começa. Com todas
 *  resolvidas, fica em `fallback`: a aba em cena, e não a 1ª — fechar o último
 *  trecho não pode devolver a pessoa ao Trecho 1. */
function firstOpenSegment(
  picks: readonly (BusSegmentPick | undefined)[],
  count: number,
  travelerIds: readonly string[],
  fallback = 0,
): number {
  const open = Array.from({ length: count }, (_, index) => index).find(
    (index) => !busSegmentComplete(picks[index], travelerIds),
  );
  return open ?? fallback;
}

/** As abas dos trechos, só quando há conexão — com o tique no trecho resolvido. */
function SegmentTabs({
  count,
  active,
  picks,
  travelerIds,
  copy,
  onChange,
}: {
  readonly count: number;
  readonly active: number;
  readonly picks: readonly (BusSegmentPick | undefined)[];
  readonly travelerIds: readonly string[];
  readonly copy: BusSeatPickerCopy;
  readonly onChange: (index: number) => void;
}): ReactNode {
  return (
    <Tabs
      value={active}
      onValueChange={(value) => {
        if (typeof value === 'number') onChange(value);
      }}
    >
      <TabsList variant="pill" aria-label={copy.segmentsLabel} className="w-full">
        {Array.from({ length: count }, (_, index) => (
          <TabsTrigger key={index} value={index}>
            {busSegmentComplete(picks[index], travelerIds) && (
              <CheckCircleIcon aria-hidden="true" weight="fill" />
            )}
            {copy.segmentLabel(index + 1)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * A escolha de poltrona POR TRECHO, inline no card da viagem (#2440, bloco
 * 3): as abas Trecho 1 / Trecho 2 quando há conexão (com o tique ao
 * concluir), a planta do ônibus de lado e o rodapé com o resumo de cada
 * trecho, o preço por pessoa, o `Selecionar` e o recolher. Uma poltrona por
 * viajante em cada trecho (as regras do pacote Flutter, `toggleBusSeat`).
 */
export function BusSeatPicker({
  segments,
  travelerIds,
  picks,
  seatClassLabel,
  price,
  copy,
  busy = false,
  layout = 'inline',
  suggestionLabel,
  seatGlyph = 'seat',
  travelers,
  recommended,
  onRetry,
  onPicksChange,
  onConfirm,
  onCollapse,
}: Readonly<BusSeatPickerProps>): ReactNode {
  const [chosen, setChosen] = useState(() => firstOpenSegment(picks, segments.length, travelerIds));
  // A viagem pode trocar para menos trechos com a aba 2 em cena: volta ao 1º.
  const active = chosen < segments.length ? chosen : 0;
  const state = segments[active] ?? { kind: 'loading' as const };
  const pick = picks[active];
  const allResolved = segments.every((_, index) => busSegmentComplete(picks[index], travelerIds));
  const missing = pick?.kind === 'seats' ? travelerIds.length - Object.keys(pick.seats).length : 0;
  const setPick = (next: BusSegmentPick | undefined) => {
    const updated = segments.map((_, index) => (index === active ? next : picks[index]));
    onPicksChange(updated);
    // Trecho resolvido leva ao próximo ainda aberto — o fluxo do frame.
    if (busSegmentComplete(next, travelerIds)) {
      setChosen(firstOpenSegment(updated, segments.length, travelerIds, active));
    }
  };
  return (
    <div className="hui-bus-seat-picker" data-layout={layout}>
      <div className="hui-bus-seat-body">
        {suggestionLabel && (
          <p className="hui-bus-seat-suggestion" role="status">
            <HeartIcon aria-hidden="true" />
            {suggestionLabel}
          </p>
        )}
        {segments.length > 1 && (
          <SegmentTabs
            count={segments.length}
            active={active}
            picks={picks}
            travelerIds={travelerIds}
            copy={copy}
            onChange={setChosen}
          />
        )}
        <SegmentBody
          key={active}
          state={state}
          pick={pick}
          travelerIds={travelerIds}
          copy={copy}
          disabled={busy}
          layout={layout}
          look={{ glyph: seatGlyph, travelers, recommended: recommended?.[active] }}
          onPick={setPick}
          onRetry={onRetry}
        />
        {travelerIds.length > 1 && missing > 0 && (
          <p className="hui-bus-seat-hint" aria-live="polite">
            {copy.pendingSeatsLabel(missing)}
          </p>
        )}
      </div>
      <div className="hui-bus-seat-footer">
        {/* A chave é o rótulo do trecho: os trechos não têm id e nunca trocam de ordem. */}
        {segments.map((_, index) => (
          <SegmentSummary
            key={copy.segmentLabel(index + 1)}
            index={index}
            pick={picks[index]}
            travelerIds={travelerIds}
            seatClassLabel={seatClassLabel}
            copy={copy}
          />
        ))}
        <p className="hui-bus-seat-price">
          <strong>{price.label}</strong>
          <small>{price.detail}</small>
        </p>
        <Button
          type="button"
          {...CONFIRM_LOOK[layout]}
          disabled={busy || !allResolved}
          onClick={onConfirm}
        >
          {copy.confirmLabel}
        </Button>
        {layout === 'inline' && (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={copy.collapseLabel}
            onClick={onCollapse}
          >
            <CaretUpIcon aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
}
